import { Box, Button, Chip, CircularProgress, Divider, IconButton, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle';
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { AsyncPaginate } from "react-select-async-paginate";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useEffect } from "react";
import Fetch from "../services/Fetch";
import { FormattedMessage } from "react-intl";
import { useConstants } from "../hooks/UseConstants";

function RequestFilter({ onClickClose, onClickConfirm, onClickStatus, status, setStatus, from, setFrom, to, setTo, teacherId, value, setValue, setTeacherId, courseName, setCourseName, filterWait, setFilterWait }) {
    const {host, language} = useConstants();
    const theme = useTheme();
    const isAr = language === "ar";

    const resetFilter = () => {
        setValue(null);
        setStatus(['pending', 'accepted', 'rejected']);
        setFrom('');
        setTo('');
        setTeacherId('');
        setCourseName('');
    };

    useEffect(() => {
        const selected = async () => {
            if (teacherId) {
                let result = await Fetch(host + `/admin/users/${teacherId}/show`, 'GET');

                if (result.status === 200) {
                    const item = result.data?.data || result.data;
                    setValue({ value: item.id, label: `${item.first_name || ""} ${item.last_name || ""}`.trim() });
                }
            }
        };

        selected();
    }, []);

    const loadOptions = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(host + `/admin/users?page=${page}&account_role=teacher${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const items = response?.data?.data?.data || [];
        const pagination = response?.data?.data || {};
        return {
            options: items.map((item) => ({
                value: item.id, label: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
            })),
            hasMore: (pagination.current_page || 1) * (pagination.per_page || items.length || 1) < (pagination.total || items.length || 0),
            additional: { page: page + 1 },
        };
    };

    const statusCards = [
        { key: "pending", color: "text-orange-400", label: <FormattedMessage id='wait_approval' /> },
        { key: "accepted", color: "text-green-400", label: <FormattedMessage id='approved' /> },
        { key: "rejected", color: "text-red-600", label: <FormattedMessage id='rejected' /> },
    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "92%", sm: "82%", md: "60%" },
                maxWidth: 780,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.18)",
            }}
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FilterAltOutlinedIcon sx={{ fontSize: 30 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='filtering_requests' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "فلترة الطلبات حسب الحالة والتاريخ واسم المعلم." : "Filter requests by status, date range, and teacher."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip label={isAr ? "فلترة متعددة" : "Multi-filter"} color="primary" variant="outlined" />
                    <Chip label={isAr ? "حالة + تاريخ + معلم" : "Status + date + teacher"} variant="outlined" />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    <FormattedMessage id='status' />
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.5, mb: 3 }}>
                    {statusCards.map((item) => (
                        <Box
                            key={item.key}
                            onClick={() => onClickStatus(item.key)}
                            sx={{
                                cursor: "pointer",
                                p: 1.5,
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: status.includes(item.key) ? "#93c5fd" : (theme.palette.mode === "dark" ? "divider" : "#e2e8f0"),
                                bgcolor: status.includes(item.key) ? (theme.palette.mode === "dark" ? "rgba(37,99,235,0.16)" : "#eff6ff") : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircleIcon className={item.color} fontSize="small" />
                                <Typography fontWeight={700}>{item.label}</Typography>
                            </Box>
                            {status.includes(item.key) ? (
                                <Box sx={{ width: 28, height: 28, borderRadius: 1.5, border: "1px solid", borderColor: "#93c5fd", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <CheckIcon fontSize="small" />
                                </Box>
                            ) : null}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                    <TextField
                        onChange={(e) => setFrom(e.target.value)}
                        type="date"
                        size="small"
                        fullWidth
                        label={<FormattedMessage id='from' />}
                        value={from}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        onChange={(e) => setTo(e.target.value)}
                        type="date"
                        size="small"
                        fullWidth
                        label={<FormattedMessage id='to' />}
                        value={to}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
                        <FormattedMessage id='teacher_name' />
                    </Typography>
                    <AsyncPaginate
                        value={value || null}
                        loadOptions={loadOptions}
                        onChange={(option) => {
                            setValue(option || null);
                            setTeacherId(option?.value || "");
                        }}
                        additional={{ page: 1 }}
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: 40,
                                borderRadius: 8,
                                borderColor: "#cbd5e1",
                            }),
                            option: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                        }}
                        isSearchable
                        placeholder={isAr ? 'اختر المعلم' : 'Select teacher'}
                    />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button onClick={resetFilter} variant="outlined" disabled={filterWait} sx={{ minWidth: 140 }}>
                        <FormattedMessage id='reset' />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={filterWait}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                        onClick={() => { setFilterWait(true); onClickConfirm(); }}
                        startIcon={filterWait ? <CircularProgress size={18} color="inherit" /> : <FilterAltOutlinedIcon />}
                    >
                        {filterWait ? (isAr ? "جاري التطبيق..." : "Applying...") : <FormattedMessage id='filtering' />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
export default RequestFilter;